import React, { useMemo, useCallback, useState, useEffect, MouseEvent } from "react";
import { RowNode } from "ag-grid-community/dist/lib/entities/rowNode";
import { ICellRendererParams } from "ag-grid-community";
import { makeStyles } from "@material-ui/core";

import { AgCheckbox, AgExpandButton } from "components/form";
import ImageIcon from "components/icons/image";
import { cancelEvent } from "../helpers/common";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { IGridContext } from "components/shipment-review/types";
import { IMAGE_URL } from "constants/product-collection";

import { DATATEST } from "constants/integration";
import theme, { rawTheme } from "styles/theme";
import { SECTIONS } from "types/view-definitions";

type RendererParams = CastProp<CastProp<ICellRendererParams, "context", IGridContext>, "data", IGridItem>;

export const ImageCellRenderer: React.FC<RendererParams> = (props) => {
    const { node, data, context } = props;
    const [isExpanded, setIsExpanded] = useState(node?.expanded || false);
    const [isSelected, setIsSelected] = useState(node?.isSelected() || false);
    const primaryImageUrl = useMemo(() => data?.fields[IMAGE_URL]?.string_value || null, [data?.fields]);
    const classes = useStyles({ primaryImageUrl });
    const imageWrapperStyles = useMemo(
        () => (primaryImageUrl ? classes.primaryImage : classes.imageContainerSmall),
        [classes.imageContainerSmall, classes.primaryImage, primaryImageUrl]
    );

    const showImage = useMemo<boolean>(() => !!context.itemView?.sections?.[SECTIONS.SMALL_IMAGE], [context.itemView]);

    const expandedChanged = useCallback(({ node }: { node: RowNode }) => setIsExpanded(node.expanded), []);
    const selectionChanged = useCallback(({ node }: { node: RowNode }) => setIsSelected(node.isSelected()), []);
    const handleExpandClick = useCallback(
        (e: MouseEvent<HTMLSpanElement>) => {
            cancelEvent(e);
            node.setExpanded(!node.expanded);
        },
        [node]
    );
    const handleSelectionClick = useCallback(
        (e: MouseEvent<HTMLInputElement>) => {
            cancelEvent(e);
            node.setSelected(!node.isSelected());
        },
        [node]
    );

    useEffect(() => {
        node.addEventListener("expandedChanged", expandedChanged);
        node.addEventListener("rowSelected", selectionChanged);
        return () => {
            node.removeEventListener("expandedChanged", expandedChanged);
            node.removeEventListener("rowSelected", selectionChanged);
        };
    }, [expandedChanged, selectionChanged, node]);

    return (
        <div
            className="ag-cell-wrapper"
            role="presentation"
            data-test={DATATEST.SHIPMENT_REVIEW.SIZING_VIEW.ROW_IMAGE_CELL}
        >
            {!node.group && (
                <>
                    <AgExpandButton expanded={isExpanded} onClick={handleExpandClick} onDoubleClick={cancelEvent} />
                    <AgCheckbox checked={isSelected} onClick={handleSelectionClick} onDoubleClick={cancelEvent} />
                </>
            )}
            {showImage && !node.group && (
                <span className="ag-cell-value" role="presentation">
                    <span className="ag-cell-wrapper ag-cell-expandable ag-row-group ag-row-group-indent-0">
                        <span className="ag-group-value">
                            <div
                                className={imageWrapperStyles}
                                data-test={
                                    primaryImageUrl ? DATATEST.SHIPMENT_REVIEW.SIZING_VIEW.PRIMARY_IMAGE : undefined
                                }
                            >
                                {!primaryImageUrl && (
                                    <ImageIcon
                                        className={classes.noImageIcon}
                                        data-test={DATATEST.SHIPMENT_REVIEW.SIZING_VIEW.NO_IMAGE}
                                    />
                                )}
                            </div>
                        </span>
                    </span>
                </span>
            )}
        </div>
    );
};

const useStyles = makeStyles({
    imageContainerSmall: {
        backgroundColor: rawTheme.palette.borderColor,
        padding: "6px",
        cursor: "pointer",
        display: "inline-block",
        position: "relative",
    },
    primaryImage: ({ primaryImageUrl }: { primaryImageUrl: string }) => ({
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundImage: `url(${primaryImageUrl})`,
        padding: "0px",
        verticalAlign: "middle",
        display: "inline-block",
        position: "relative",
        width: 40,
        height: 40,
    }),
    noImageIcon: {
        color: `${theme.palette.grey} !important`,
        verticalAlign: "middle",
    },
});
